import { useState, useEffect, useRef, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, HelpCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Tutorial context for global access
const TutorialContext = createContext(null);

export const useTutorial = () => useContext(TutorialContext);

// Tutorial steps configuration — rewritten Feb 2026, tightened tone.
// Direct, no marketing-speak. Mirrors current nav (slim public nav,
// TopTasksBar persona switcher, ⌘K Quick Search) and current platform
// features (Capital Access wedge, three donation rails, PROOF, /tools).
const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to the FramewUrk.',
    description: "A 60-second tour of how this platform works for founders, nonprofit EDs, and volunteers. Skip anytime — we'll save your place.",
    target: null,
    position: 'center',
    icon: '👋',
  },
  {
    id: 'top-tasks',
    title: 'Pick your role.',
    description: "Founder, Nonprofit ED, Donor, Veteran, or Volunteer. We'll swap in the four most relevant actions for you. Saved across visits.",
    target: '[data-testid="top-tasks-bar"]',
    position: 'bottom',
    icon: '🎯',
  },
  {
    id: 'find-capital',
    title: 'Find Capital.',
    description: "Match to grants with the AI Concierge. Compare lenders. Track capital readiness. Federal contracting, too.",
    target: '[data-testid="nav-trigger-find-capital"]',
    fallbackTarget: '.hidden.lg\\:flex',
    position: 'bottom',
    icon: '💰',
  },
  {
    id: 'proof-system',
    title: 'PROOF credentials.',
    description: "Every grant landed, hour volunteered, and milestone hit becomes a verifiable credential. Shareable on LinkedIn. Embeddable on your site.",
    target: '[data-testid="nav-trigger-prove-impact"]',
    fallbackTarget: 'a[href="/proof"]',
    position: 'bottom',
    icon: '🏅',
  },
  {
    id: 'volunteer-hub',
    title: 'Volunteer Hub.',
    description: "Find missions to join, or post one. Every volunteer hour earns a verifiable PROOF credential.",
    target: '[data-testid="nav-trigger-community"]',
    fallbackTarget: 'a[href="/volunteer-hub"]',
    position: 'bottom',
    icon: '💝',
  },
  {
    id: 'three-rails',
    title: 'Three donation rails.',
    description: "Stripe (Apple Pay), Givebutter (peer-to-peer), Zeffy (zero fees). Tax receipts sent automatically.",
    target: '[data-testid="nav-give-primary"]',
    position: 'bottom',
    icon: '❤️',
  },
  {
    id: 'quick-search',
    title: 'Quick Search.',
    description: "Press ⌘K (Mac) or Ctrl+K (Windows) anywhere to search 80+ tools, programs, and concierges.",
    target: '[data-testid="header-quick-search-btn"]',
    position: 'bottom',
    icon: '🔍',
  },
  {
    id: 'dashboard',
    title: 'Your dashboard.',
    description: "Personal trackers, AI workspaces, your Canonical Org Profile, and the My Tools section. Available after sign-in.",
    target: 'a[href="/dashboard"], a[href="/login"]',
    fallbackTarget: 'a[href="/register"]',
    position: 'bottom',
    icon: '📊',
  },
  {
    id: 'complete',
    title: "You're set.",
    description: "Click the Help icon anytime to replay this tour.",
    target: null,
    position: 'center',
    icon: '🚀',
  },
];

// Tooltip positioning component
function TutorialTooltip({ step, onNext, onPrev, onSkip, currentIndex, totalSteps }) {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [arrowPosition, setArrowPosition] = useState('bottom');
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (step.target && step.position !== 'center') {
      const target = document.querySelector(step.target) ||
                     (step.fallbackTarget && document.querySelector(step.fallbackTarget));

      if (target) {
        // Bring the target into view first so positioning has a stable rect.
        try {
          target.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'center' });
        } catch (_) {
          target.scrollIntoView();
        }

        const rect = target.getBoundingClientRect();
        const tooltipWidth = 320;
        // Real rendered height once mounted, otherwise a realistic fallback.
        // The previous 200 was too small and pushed the Next button off-screen
        // on shorter viewports.
        const tooltipHeight = tooltipRef.current?.offsetHeight || 360;
        const padding = 16;

        let top;
        let left;
        let arrow = 'top';
        let pos = step.position;

        // Auto-flip bottom→top when there isn't enough room below the target.
        if (pos === 'bottom' && rect.bottom + padding + tooltipHeight > window.innerHeight - padding) {
          if (rect.top - padding - tooltipHeight > padding) pos = 'top';
        }
        // Auto-flip top→bottom when there isn't enough room above either.
        if (pos === 'top' && rect.top - padding - tooltipHeight < padding) {
          if (rect.bottom + padding + tooltipHeight < window.innerHeight - padding) pos = 'bottom';
        }

        switch (pos) {
          case 'bottom':
            top = rect.bottom + padding;
            left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
            arrow = 'top';
            break;
          case 'top':
            top = rect.top - tooltipHeight - padding;
            left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
            arrow = 'bottom';
            break;
          case 'left':
            top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
            left = rect.left - tooltipWidth - padding;
            arrow = 'right';
            break;
          case 'right':
            top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
            left = rect.right + padding;
            arrow = 'left';
            break;
          default:
            top = window.innerHeight / 2 - tooltipHeight / 2;
            left = window.innerWidth / 2 - tooltipWidth / 2;
        }

        // Final viewport clamp using REAL tooltip height.
        left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));
        top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));

        setPosition({ top, left });
        setArrowPosition(arrow);

        // Highlight the target
        target.style.position = 'relative';
        target.style.zIndex = '10001';
        target.classList.add('tutorial-highlight');
      }
    }

    return () => {
      // Clean up highlight
      const highlighted = document.querySelector('.tutorial-highlight');
      if (highlighted) {
        highlighted.style.zIndex = '';
        highlighted.classList.remove('tutorial-highlight');
      }
    };
  }, [step]);

  // A11y: Escape closes the tour, Tab traps focus inside the card so the user
  // can keyboard-navigate between Back / Next / Skip without escaping into the
  // page underneath.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onSkip();
        return;
      }
      if (e.key === 'Tab' && tooltipRef.current) {
        const focusables = tooltipRef.current.querySelectorAll(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);

    // Auto-focus the primary action button when a step opens so keyboard users
    // can press Enter immediately to advance.
    const t = setTimeout(() => {
      const card = tooltipRef.current;
      if (!card) return;
      const primary = card.querySelector('button.bg-\\[\\#006847\\]') ||
                      card.querySelector('button:not([disabled])');
      if (primary) primary.focus({ preventScroll: true });
    }, 60);

    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [step, onSkip]);

  const isCentered = step.position === 'center' || !step.target;

  return (
    <motion.div
      ref={tooltipRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorial-title"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`fixed z-[10002] ${isCentered ? 'inset-0 flex items-center justify-center' : ''}`}
      style={!isCentered ? { top: position.top, left: position.left } : {}}
    >
      <div className={`bg-white rounded-2xl shadow-2xl border border-gray-200 ${isCentered ? 'max-w-md mx-4' : 'w-80'}`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#006847] to-[#388E3C] text-white p-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{step.icon}</span>
              <h3 className="font-bold text-lg" id="tutorial-title">{step.title}</h3>
            </div>
            <button onClick={onSkip} aria-label="Skip tour" className="text-white/70 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-gray-600 mb-4">{step.description}</p>

          {/* Progress dots */}
          <div className="flex justify-center gap-1.5 mb-4">
            {Array.from({ length: totalSteps }).map((_, idx) => (
              <div
                key={`item-${idx}`}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx === currentIndex ? 'bg-[#006847]' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={onPrev}
              disabled={currentIndex === 0}
              className="text-gray-500"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            
            <span className="text-sm text-gray-400">
              {currentIndex + 1} / {totalSteps}
            </span>

            {currentIndex === totalSteps - 1 ? (
              <Button
                size="sm"
                onClick={onSkip}
                className="bg-[#006847] hover:bg-[#004d35]"
              >
                Get Started
                <Sparkles className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={onNext}
                className="bg-[#006847] hover:bg-[#004d35]"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>

        {/* Arrow pointer for non-centered tooltips */}
        {!isCentered && (
          <div
            className={`absolute w-3 h-3 bg-white border border-gray-200 transform rotate-45 ${
              arrowPosition === 'top' ? '-top-1.5 left-1/2 -translate-x-1/2 border-b-0 border-r-0' :
              arrowPosition === 'bottom' ? '-bottom-1.5 left-1/2 -translate-x-1/2 border-t-0 border-l-0' :
              arrowPosition === 'left' ? 'top-1/2 -left-1.5 -translate-y-1/2 border-t-0 border-r-0' :
              'top-1/2 -right-1.5 -translate-y-1/2 border-b-0 border-l-0'
            }`}
          />
        )}
      </div>
    </motion.div>
  );
}

// Help button component
export function HelpButton() {
  const { startTutorial } = useTutorial();

  return (
    <motion.button
      onClick={startTutorial}
      className="fixed bottom-[188px] md:bottom-[92px] right-4 md:right-6 z-[51] bg-[#006847] text-white p-3 rounded-full shadow-lg hover:bg-[#004d35] transition-colors"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      data-testid="help-button"
      title="Start Tutorial"
    >
      <HelpCircle className="h-5 w-5" />
    </motion.button>
  );
}

// Tutorial Provider component
export function TutorialProvider({ children }) {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSeenTutorial, setHasSeenTutorial] = useState(false);

  // Check if user has seen tutorial. We bumped the storage key when the
  // tutorial was rewritten Feb 2026 so anyone who saw the old version is
  // re-greeted with the new one.
  useEffect(() => {
    const seen = localStorage.getItem('dowurk_tutorial_seen_v2');
    setHasSeenTutorial(!!seen);
  }, []);

  // Auto-start tutorial for first-time visitors (logged in OR anonymous) on
  // the homepage. Deep-link arrivals (e.g. /proof/resilience-pathway) skip it
  // so the primary CTA isn't blocked. Help button always replays manually.
  useEffect(() => {
    if (hasSeenTutorial) return;
    const onHomePage = typeof window !== 'undefined' && (window.location.pathname === '/' || window.location.pathname === '');
    if (!onHomePage) return;
    const timer = setTimeout(() => {
      setIsActive(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, [hasSeenTutorial]);

  const startTutorial = () => {
    setCurrentStep(0);
    setIsActive(true);
  };

  const endTutorial = () => {
    setIsActive(false);
    setCurrentStep(0);
    setHasSeenTutorial(true);
    localStorage.setItem('dowurk_tutorial_seen_v2', 'true');
  };

  const nextStep = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      endTutorial();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  return (
    <TutorialContext.Provider value={{ startTutorial, isActive }}>
      {children}
      
      <AnimatePresence>
        {isActive && (
          <>
            {/* Backdrop overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[10000]"
              onClick={endTutorial}
            />

            {/* Tutorial tooltip */}
            <TutorialTooltip
              step={TUTORIAL_STEPS[currentStep]}
              currentIndex={currentStep}
              totalSteps={TUTORIAL_STEPS.length}
              onNext={nextStep}
              onPrev={prevStep}
              onSkip={endTutorial}
            />
          </>
        )}
      </AnimatePresence>

      {/* Global CSS for tutorial highlight */}
      <style>{`
        .tutorial-highlight {
          box-shadow: 0 0 0 4px rgba(0, 104, 71, 0.4), 0 0 20px rgba(0, 104, 71, 0.2);
          border-radius: 8px;
        }
      `}</style>
    </TutorialContext.Provider>
  );
}

export default TutorialProvider;
